import React, { useState } from "react";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";
import {
  Check,
  RotateCcw,
  Award,
  Heart,
  Brain,
  Activity,
  Pill,
  Delete,
} from "lucide-react";

const MedicalCrossword = ({ onBack, userData, saveUserData }) => {
  const [gamePhase, setGamePhase] = useState("theme"); // theme, playing, complete
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [puzzleIndex, setPuzzleIndex] = useState(0);
  const [grid, setGrid] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [selectedCell, setSelectedCell] = useState(null);
  const [direction, setDirection] = useState("across");
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const themes = [
    {
      id: "anatomy",
      name: "Anatomy",
      description: "Body structures and organs",
      icon: Heart,
      color: "from-red-100 to-pink-100",
      borderColor: "border-red-300",
      iconColor: "text-red-600",
    },
    {
      id: "pharmacology",
      name: "Pharmacology",
      description: "Drugs and medications",
      icon: Pill,
      color: "from-blue-100 to-cyan-100",
      borderColor: "border-blue-300",
      iconColor: "text-blue-600",
    },
    {
      id: "pathology",
      name: "Pathology",
      description: "Diseases and conditions",
      icon: Activity,
      color: "from-green-100 to-emerald-100",
      borderColor: "border-green-300",
      iconColor: "text-green-600",
    },
    {
      id: "neuroscience",
      name: "Neuroscience",
      description: "Brain and nervous system",
      icon: Brain,
      color: "from-purple-100 to-violet-100",
      borderColor: "border-purple-300",
      iconColor: "text-purple-600",
    },
  ];

  const crosswordData = {
    anatomy: [
      {
        size: 12,
        words: [
          {
            word: "ALVEOLI",
            clue: "Where the lungs and the blood exchange oxygen",
            row: 8,
            col: 2,
            direction: "across",
            number: 6,
          },
          {
            word: "STAPES",
            clue: "smallest bone in the human body",
            row: 3,
            col: 5,
            direction: "across",
            number: 2,
          },
          {
            word: "PFO",
            clue: "abbreviation for a congenital small hole or flap between the atriums",
            row: 0,
            col: 3,
            direction: "across",
            number: 1,
          },
          {
            word: "PLEURA",
            clue: "a thin, double-layered, serous membrane around the lungs",
            row: 0,
            col: 3,
            direction: "down",
            number: 1,
          },
          {
            word: "MANUBRIUM",
            clue: "Upper part of the sternum",
            row: 5,
            col: 2,
            direction: "across",
            number: 4,
          },
          {
            word: "CHOROID",
            clue: "modulates intraocular pressure",
            row: 11,
            col: 0,
            direction: "across",
            number: 7,
          },
          {
            word: "ULNAR",
            clue: "artery that arises from the brachial artery, in the cubital fossa",
            row: 7,
            col: 3,
            direction: "down",
            number: 5,
          },
          {
            word: "AURICLE",
            clue: 'the "ear" part of the heart',
            row: 3,
            col: 7,
            direction: "down",
            number: 3,
          },
        ],
        coins: 15,
        xp: 30,
      },
      {
        size: 14,
        words: [
          {
            word: "DIAPHRAGM",
            clue: "What muscle separates the thoracic cavity from the abdominal cavity",
            row: 0,
            col: 2,
            direction: "down",
            number: 1,
          },
          {
            word: "PHALANGES",
            clue: "bones in the fingers and toes",
            row: 2,
            col: 0,
            direction: "across",
            number: 2,
          },
          {
            word: "JEJUNUM",
            clue: "part of the small intestine between the duodenum and ileum",
            row: 4,
            col: 5,
            direction: "down",
            number: 3,
          },
          {
            word: "AUERBACH",
            clue: "plexus that controls gastrointestinal motility",
            row: 4,
            col: 8,
            direction: "down",
            number: 4,
          },
          {
            word: "CEREBELLUM",
            clue: "part of the brain that coordinates movement and balance",
            row: 5,
            col: 0,
            direction: "across",
            number: 5,
          },
          {
            word: "TREITZ",
            clue: "ligament that supports the duodenojejunal flexure",
            row: 7,
            col: 10,
            direction: "down",
            number: 6,
          },
          {
            word: "PATELLA",
            clue: "the kneecap",
            row: 9,
            col: 7,
            direction: "across",
            number: 7,
          },
          {
            word: "LIVER",
            clue: "detoxifies chemicals and metabolizes drugs",
            row: 9,
            col: 12,
            direction: "down",
            number: 8,
          },
        ],
        coins: 15,
        xp: 30,
      },
    ],

    pharmacology: [
      {
        size: 13,
        words: [
          {
            word: "ALBUTEROL",
            clue: "Bronchodilator for asthma treatment",
            row: 0,
            col: 1,
            direction: "across",
            number: 1,
          },
          {
            word: "ACYCLOVIR",
            clue: "Antiviral medication",
            row: 0,
            col: 1,
            direction: "down",
            number: 1,
          },
          {
            word: "ARB",
            clue: "Class of drugs that block angiotensin II receptors",
            row: 8,
            col: 0,
            direction: "across",
            number: 2,
          },
          {
            word: "LEVODOPA",
            clue: "Antiparkinsonian medication",
            row: 4,
            col: 1,
            direction: "across",
            number: 3,
          },
          {
            word: "WARFARIN",
            clue: "Anticoagulant medication",
            row: 3,
            col: 8,
            direction: "down",
            number: 4,
          },
          {
            word: "STATIN",
            clue: "Common medication for lowering cholesterol",
            row: 7,
            col: 6,
            direction: "across",
            number: 5,
          },
          {
            word: "ASPIRIN",
            clue: "Nonsteroidal anti-inflammatory drug (NSAID)",
            row: 4,
            col: 10,
            direction: "down",
            number: 6,
          },
          {
            word: "OXYCODONE",
            clue: "Opioid pain medication",
            row: 4,
            col: 4,
            direction: "down",
            number: 7,
          },
          {
            word: "OMEPRAZOLE",
            clue: "Proton pump inhibitor for acid reflux",
            row: 12,
            col: 2,
            direction: "across",
            number: 8,
          },
        ],
        coins: 20,
        xp: 40,
      },
      {
        size: 14,
        words: [
          {
            word: "PROPOFOL",
            clue: "Sedative-hypnotic agent used for induction and maintenance of anesthesia",
            row: 1,
            col: 0,
            direction: "across",
            number: 1,
          },
          {
            word: "TRAMADOL",
            clue: "Opioid analgesic used to treat moderate to severe pain",
            row: 0,
            col: 1,
            direction: "down",
            number: 2,
          },
          {
            word: "RITUXIMAB",
            clue: "Monoclonal antibody used to treat certain autoimmune diseases and cancers",
            row: 5,
            col: 3,
            direction: "across",
            number: 3,
          },
          {
            word: "DIGOXIN",
            clue: "Cardiac glycoside used to treat heart conditions",
            row: 3,
            col: 7,
            direction: "across",
            number: 4,
          },
          {
            word: "FUROSEMIDE",
            clue: "Loop diuretic used to treat edema and hypertension",
            row: 4,
            col: 6,
            direction: "down",
            number: 6,
          },
          {
            word: "LOSARTAN",
            clue: "Angiotensin II receptor blocker used to treat high blood pressure",
            row: 2,
            col: 10,
            direction: "down",
            number: 7,
          },
          {
            word: "GABAPENTIN",
            clue: "Anticonvulsant and nerve pain medication",
            row: 9,
            col: 1,
            direction: "across",
            number: 11,
          },
          {
            word: "CEFTRIAXONE",
            clue: "Cephalosporin antibiotic",
            row: 11,
            col: 1,
            direction: "across",
            number: 8,
          },
          {
            word: "MAGNESIUM",
            clue: "Mineral sometimes used to treat constipation",
            row: 13,
            col: 2,
            direction: "across",
            number: 9,
          },
          {
            word: "NALOXONE",
            clue: "Opioid overdose reversal agent",
            row: 3,
            col: 13,
            direction: "down",
            number: 10,
          },
        ],
        coins: 20,
        xp: 40,
      },
      {
        size: 15,
        words: [
          {
            word: "PREDNISONE",
            clue: "Corticosteroid used to treat inflammation and autoimmune conditions",
            row: 0,
            col: 3,
            direction: "across",
            number: 1,
          },
          {
            word: "DOXYCYCLINE",
            clue: "Tetracycline antibiotic used to treat bacterial infections",
            row: 3,
            col: 1,
            direction: "across",
            number: 2,
          },
          {
            word: "SERTRALINE",
            clue: "Selective serotonin reuptake inhibitor (SSRI) used to treat depression and anxiety",
            row: 5,
            col: 0,
            direction: "across",
            number: 3,
          },
          {
            word: "OXYCODONE",
            clue: "Opioid analgesic used to treat moderate to severe pain",
            row: 10,
            col: 5,
            direction: "across",
            number: 4,
          },
          {
            word: "ASPIRIN",
            clue: "COX-1 inhibitor used for pain relief and cardiovascular protection",
            row: 4,
            col: 0,
            direction: "down",
            number: 5,
          },
          {
            word: "APIXABAN",
            clue: "Anticoagulant used to prevent blood clots",
            row: 7,
            col: 3,
            direction: "down",
            number: 6,
          },
          {
            word: "CLINDAMYCIN",
            clue: "Antibiotic used to treat bacterial infections, especially those caused by anaerobic bacteria and certain protozoal infections",
            row: 3,
            col: 7,
            direction: "down",
            number: 7,
          },
          {
            word: "SSRI",
            clue: "Class of drugs that block the reuptake of serotonin in the brain",
            row: 0,
            col: 9,
            direction: "down",
            number: 8,
          },
          {
            word: "LEVOTHYROXINE",
            clue: "Thyroid hormone used to treat hypothyroidism",
            row: 2,
            col: 11,
            direction: "down",
            number: 9,
          },
          {
            word: "LOPERAMIDE",
            clue: "Medication used to treat diarrhea",
            row: 1,
            col: 13,
            direction: "down",
            number: 10,
          },
        ],
        coins: 20,
        xp: 40,
      },
    ],
    pathology: [
      {
        size: 14,
        words: [
          {
            word: "EOSINOPHILIA",
            clue: "High levels of eosinophils in the blood",
            row: 0,
            col: 0,
            direction: "across",
            number: 5,
          },
          {
            word: "HYPOXIA",
            clue: "Low oxygen levels in tissues",
            row: 0,
            col: 7,
            direction: "down",
            number: 2,
          },
          {
            word: "HYPEREMIA",
            clue: "Excess blood in vessels",
            row: 2,
            col: 5,
            direction: "across",
            number: 3,
          },
          {
            word: "HEMOSIDERIN",
            clue: "Iron-storage complex",
            row: 5,
            col: 2,
            direction: "across",
            number: 9,
          },
          {
            word: "EXUDATE",
            clue: "Fluid rich in protein and cellular debris",
            row: 0,
            col: 0,
            direction: "down",
            number: 5,
          },
          {
            word: "MELANIN",
            clue: "Pigment responsible for skin color",
            row: 5,
            col: 4,
            direction: "down",
            number: 6,
          },
          {
            word: "ANOXIA",
            clue: "Complete lack of oxygen supply to tissues",
            row: 8,
            col: 4,
            direction: "across",
            number: 7,
          },
          {
            word: "NECROSIS",
            clue: "Death of tissue",
            row: 11,
            col: 4,
            direction: "across",
            number: 8,
          },
          {
            word: "HYDROPS",
            clue: "Abnormal accumulation of fluid in a body cavity or tissue",
            row: 5,
            col: 2,
            direction: "down",
            number: 9,
          },
          {
            word: "NODULE",
            clue: "Small lump or mass of tissue",
            row: 5,
            col: 12,
            direction: "down",
            number: 10,
          },
        ],
        coins: 20,
        xp: 40,
      },
      {
        size: 13,
        words: [
          {
            word: "ECCHYMOSIS",
            clue: "Discoloration of skin due to bleeding underneath",
            row: 0,
            col: 0,
            direction: "down",
            number: 1,
          },
          {
            word: "APOPTOSIS",
            clue: "Programmed cell death",
            row: 1,
            col: 2,
            direction: "down",
            number: 2,
          },
          {
            word: "METAPLASIA",
            clue: "Change in the type of adult cells",
            row: 5,
            col: 0,
            direction: "across",
            number: 3,
          },
          {
            word: "ATROPHY",
            clue: "When organs or cells shrink and lose function",
            row: 1,
            col: 4,
            direction: "down",
            number: 4,
          },
          {
            word: "PETECHIAE",
            clue: "Small red or purple spots caused by bleeding into the skin",
            row: 2,
            col: 2,
            direction: "across",
            number: 5,
          },
          {
            word: "FIBROSIS",
            clue: "Formation of excess fibrous connective tissue",
            row: 4,
            col: 8,
            direction: "down",
            number: 6,
          },
          {
            word: "THROMBUS",
            clue: "Blood clot formed in a vessel",
            row: 8,
            col: 5,
            direction: "across",
            number: 10,
          },
          {
            word: "GRANULOMA",
            clue: "A cluster of immune cells, esp. macrophages, formed in response to chronic inflammation",
            row: 4,
            col: 11,
            direction: "down",
            number: 8,
          },
          {
            word: "TOXIN",
            clue: "Poisonous substance produced by living organisms",
            row: 8,
            col: 5,
            direction: "down",
            number: 10,
          },
          {
            word: "SEPSIS",
            clue: "A life-threatening condition caused by the body's response to infection",
            row: 11,
            col: 1,
            direction: "across",
            number: 11,
          },
        ],
        coins: 20,
        xp: 40,
      },
    ],
    neuroscience: [
      {
        size: 15,
        words: [
          {
            word: "SYNAPSE",
            clue: "Junction between neurons",
            row: 0,
            col: 2,
            direction: "down",
            number: 1,
          },
          {
            word: "HYPOTHALAMUS",
            clue: "Brain region that regulates homeostasis, hormones, and autonomic functions",
            row: 4,
            col: 0,
            direction: "across",
            number: 3,
          },
          {
            word: "HIPPOCAMPUS",
            clue: "Limbic system structure involved in memory formation",
            row: 4,
            col: 0,
            direction: "down",
            number: 3,
          },
          {
            word: "GABA",
            clue: "Main inhibitory neurotransmitter in the brain",
            row: 8,
            col: 2,
            direction: "down",
            number: 4,
          },
          {
            word: "AXON",
            clue: "Long projection of a neuron that conducts electrical impulses",
            row: 9,
            col: 2,
            direction: "across",
            number: 5,
          },
          {
            word: "ACETYLCHOLINE",
            clue: "Neurotransmitter involved in muscle activation",
            row: 1,
            col: 4,
            direction: "down",
            number: 8,
          },
          {
            word: "ASTROCYTE",
            clue: "Star-shaped glial cell in the brain and spinal cord",
            row: 1,
            col: 4,
            direction: "across",
            number: 8,
          },
          {
            word: "AMYGDALA",
            clue: "Almond-shaped set of neurons involved in emotion processing",
            row: 3,
            col: 9,
            direction: "down",
            number: 9,
          },
          {
            word: "MIDBRAIN",
            clue: "Part of the brainstem involved in vision, hearing, and motor control",
            row: 7,
            col: 7,
            direction: "across",
            number: 10,
          },
          {
            word: "APHASIA",
            clue: "Loss of ability to understand or express speech",
            row: 2,
            col: 13,
            direction: "down",
            number: 11,
          },
          {
            word: "ENDORPHIN",
            clue: "Natural painkiller produced by the body",
            row: 13,
            col: 4,
            direction: "across",
            number: 12,
          },
        ],
        coins: 25,
        xp: 50,
      },
    ],
  };

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    setPuzzleIndex(0);
    const puzzleData = crosswordData[themeId][0];
    initializeGrid(puzzleData);
    setGamePhase("playing");
  };

  const handlePuzzleChange = (newIndex) => {
    setPuzzleIndex(newIndex);
    const puzzleData = crosswordData[selectedTheme][newIndex];
    initializeGrid(puzzleData);
    setUserAnswers({});
    setSelectedCell(null);
    setShowResults(false);
    setScore(0);
  };

  const initializeGrid = (puzzleData) => {
    const newGrid = Array(puzzleData.size)
      .fill(null)
      .map(() =>
        Array(puzzleData.size)
          .fill(null)
          .map(() => ({ letter: null, number: null, isBlack: true })),
      );

    puzzleData.words.forEach((wordObj) => {
      for (let i = 0; i < wordObj.word.length; i++) {
        const row =
          wordObj.direction === "across" ? wordObj.row : wordObj.row + i;
        const col =
          wordObj.direction === "across" ? wordObj.col + i : wordObj.col;

        if (row < puzzleData.size && col < puzzleData.size) {
          newGrid[row][col] = {
            letter: wordObj.word[i],
            number: i === 0 ? wordObj.number : newGrid[row][col].number,
            isBlack: false,
          };
        }
      }
    });

    setGrid(newGrid);
    setUserAnswers({});
  };

  const handleCellClick = (row, col) => {
    if (grid[row][col].isBlack) return;

    if (selectedCell && selectedCell.row === row && selectedCell.col === col) {
      setDirection(direction === "across" ? "down" : "across");
    } else {
      setSelectedCell({ row, col });
    }
  };

  const handleKeyPress = (e, row, col) => {
    if (grid[row][col].isBlack) return;

    const key = e.key.toUpperCase();

    if (/^[A-Z]$/.test(key)) {
      const newAnswers = { ...userAnswers, [`${row}-${col}`]: key };
      setUserAnswers(newAnswers);
      moveToNextCell(row, col);
    } else if (e.key === "Backspace") {
      const newAnswers = { ...userAnswers };
      delete newAnswers[`${row}-${col}`];
      setUserAnswers(newAnswers);
      moveToPreviousCell(row, col);
    }
  };

  const handleVirtualKeyPress = (key) => {
    if (!selectedCell) return;

    const { row, col } = selectedCell;
    if (grid[row][col].isBlack) return;

    if (key === "BACKSPACE") {
      const newAnswers = { ...userAnswers };
      delete newAnswers[`${row}-${col}`];
      setUserAnswers(newAnswers);
      moveToPreviousCell(row, col);
    } else {
      const newAnswers = { ...userAnswers, [`${row}-${col}`]: key };
      setUserAnswers(newAnswers);
      moveToNextCell(row, col);
    }
  };

  const moveToNextCell = (row, col) => {
    const puzzleData = crosswordData[selectedTheme];

    if (direction === "across") {
      for (let c = col + 1; c < puzzleData.size; c++) {
        if (!grid[row][c].isBlack) {
          setSelectedCell({ row, col: c });
          return;
        }
      }
    } else {
      for (let r = row + 1; r < puzzleData.size; r++) {
        if (!grid[r][col].isBlack) {
          setSelectedCell({ row: r, col });
          return;
        }
      }
    }
  };

  const moveToPreviousCell = (row, col) => {
    if (direction === "across") {
      for (let c = col - 1; c >= 0; c--) {
        if (!grid[row][c].isBlack) {
          setSelectedCell({ row, col: c });
          return;
        }
      }
    } else {
      for (let r = row - 1; r >= 0; r--) {
        if (!grid[r][col].isBlack) {
          setSelectedCell({ row: r, col });
          return;
        }
      }
    }
  };

  const handleCheckAnswers = () => {
    let correctCount = 0;
    let totalCells = 0;

    grid.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (!cell.isBlack) {
          totalCells++;
          const userAnswer = userAnswers[`${rowIndex}-${colIndex}`];
          if (userAnswer === cell.letter) {
            correctCount++;
          }
        }
      });
    });

    setScore(correctCount);
    setShowResults(true);

    const puzzleData = crosswordData[selectedTheme];
    const percentage = (correctCount / totalCells) * 100;

    if (percentage === 100) {
      const newUserData = {
        ...userData,
        coins: userData.coins + puzzleData.coins,
        xp: userData.xp + puzzleData.xp,
        avatarStats: applyBoost(userData.avatarStats, GAME_BOOST),
      };
      saveUserData(newUserData);
    }
  };

  const handleRestart = () => {
    setGamePhase("theme");
    setSelectedTheme(null);
    setGrid([]);
    setUserAnswers({});
    setSelectedCell(null);
    setShowResults(false);
    setScore(0);
  };

  // Theme Selection Screen
  if (gamePhase === "theme") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-crossword.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-amber-900/80 to-orange-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Medical Crossword
              </h1>
              <p className="text-gray-600">
                Choose a theme to start solving the crossword puzzle
              </p>
            </div>

            <h2 className="text-2xl font-bold text-white mb-6">Select Theme</h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {themes.map((theme) => {
                const ThemeIcon = theme.icon;
                return (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeSelect(theme.id)}
                    className={
                      "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                      theme.color +
                      " " +
                      theme.borderColor
                    }
                  >
                    <div className="flex justify-center mb-4">
                      <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                        <ThemeIcon className={"w-8 h-8 " + theme.iconColor} />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                      {theme.name}
                    </h3>
                    <p className="text-sm text-gray-600 text-center">
                      {theme.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Playing Phase
  const puzzleData = crosswordData[selectedTheme][puzzleIndex];
  const totalPuzzles = crosswordData[selectedTheme].length;
  const totalCells = grid.flat().filter((cell) => !cell.isBlack).length;
  const percentage =
    totalCells > 0 ? Math.round((score / totalCells) * 100) : 0;

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-crossword.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-amber-900/80 to-orange-900/80">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button
            onClick={() => setGamePhase("theme")}
            className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
          >
            ← Back to Themes
          </button>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 capitalize">
                  {selectedTheme} Crossword
                </h1>
                <p className="text-sm text-gray-600">
                  Fill in the crossword using the clues below
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => handlePuzzleChange(puzzleIndex - 1)}
                    disabled={puzzleIndex === 0}
                    className="px-3 py-1 text-sm rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Puzzle {puzzleIndex + 1} of {totalPuzzles}
                  </span>
                  <button
                    onClick={() => handlePuzzleChange(puzzleIndex + 1)}
                    disabled={puzzleIndex === totalPuzzles - 1}
                    className="px-3 py-1 text-sm rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                {!showResults && (
                  <button
                    onClick={handleCheckAnswers}
                    className="bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                  >
                    Check Answers
                  </button>
                )}
                <button
                  onClick={handleRestart}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  New Puzzle
                </button>
              </div>
            </div>
          </div>

          {showResults && (
            <div
              className={
                "mb-6 p-6 rounded-xl border-2 " +
                (percentage === 100
                  ? "bg-green-50 border-green-500"
                  : "bg-yellow-50 border-yellow-500")
              }
            >
              <div className="flex items-center gap-3 mb-2">
                {percentage === 100 ? (
                  <Check className="w-8 h-8 text-green-600" />
                ) : (
                  <Award className="w-8 h-8 text-yellow-600" />
                )}
                <h3 className="text-2xl font-bold text-gray-800">
                  {percentage === 100 ? "Perfect Score!" : "Good Effort!"}
                </h3>
              </div>
              <p className="text-gray-700 mb-2">
                You got {score} out of {totalCells} correct ({percentage}%)
              </p>
              {percentage === 100 && (
                <p className="text-sm text-green-700">
                  +{puzzleData.coins} coins, +{puzzleData.xp} XP earned!
                </p>
              )}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Crossword Grid */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="inline-block">
                {grid.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex">
                    {row.map((cell, colIndex) => {
                      const isSelected =
                        selectedCell?.row === rowIndex &&
                        selectedCell?.col === colIndex;
                      const userAnswer = userAnswers[`${rowIndex}-${colIndex}`];
                      const isCorrect =
                        showResults && userAnswer === cell.letter;
                      const isIncorrect =
                        showResults && userAnswer && userAnswer !== cell.letter;

                      return (
                        <div
                          key={colIndex}
                          className={
                            "w-10 h-10 border border-gray-400 relative flex items-center justify-center text-lg font-bold cursor-pointer " +
                            (cell.isBlack
                              ? "bg-gray-800"
                              : isSelected
                                ? "bg-teal-200"
                                : isCorrect
                                  ? "bg-green-100"
                                  : isIncorrect
                                    ? "bg-red-100"
                                    : "bg-white hover:bg-gray-50")
                          }
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                          onKeyDown={(e) =>
                            handleKeyPress(e, rowIndex, colIndex)
                          }
                          tabIndex={cell.isBlack ? -1 : 0}
                        >
                          {!cell.isBlack && (
                            <>
                              {cell.number && (
                                <span className="absolute top-0 left-0.5 text-[8px] text-gray-600">
                                  {cell.number}
                                </span>
                              )}
                              <span className="text-gray-800">
                                {showResults ? cell.letter : userAnswer || ""}
                              </span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* On-Screen Keyboard for Mobile */}
              {!showResults && (
                <div className="mt-6">
                  <div className="text-xs text-gray-600 mb-2 text-center">
                    Current direction:{" "}
                    <span className="font-semibold capitalize">
                      {direction}
                    </span>
                    {selectedCell && (
                      <span className="ml-2">
                        (Tap cell again to change direction)
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-10 gap-1">
                      {["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"].map(
                        (letter) => (
                          <button
                            key={letter}
                            onClick={() => handleVirtualKeyPress(letter)}
                            disabled={!selectedCell}
                            className={
                              "w-full aspect-square rounded text-sm font-semibold transition-colors " +
                              (selectedCell
                                ? "bg-teal-600 text-white hover:bg-teal-700 active:bg-teal-800"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed")
                            }
                          >
                            {letter}
                          </button>
                        ),
                      )}
                    </div>
                    <div className="grid grid-cols-9 gap-1 px-4">
                      {["A", "S", "D", "F", "G", "H", "J", "K", "L"].map(
                        (letter) => (
                          <button
                            key={letter}
                            onClick={() => handleVirtualKeyPress(letter)}
                            disabled={!selectedCell}
                            className={
                              "w-full aspect-square rounded text-sm font-semibold transition-colors " +
                              (selectedCell
                                ? "bg-teal-600 text-white hover:bg-teal-700 active:bg-teal-800"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed")
                            }
                          >
                            {letter}
                          </button>
                        ),
                      )}
                    </div>
                    <div className="grid grid-cols-9 gap-1 px-8">
                      {["Z", "X", "C", "V", "B", "N", "M"].map((letter) => (
                        <button
                          key={letter}
                          onClick={() => handleVirtualKeyPress(letter)}
                          disabled={!selectedCell}
                          className={
                            "w-full aspect-square rounded text-sm font-semibold transition-colors " +
                            (selectedCell
                              ? "bg-teal-600 text-white hover:bg-teal-700 active:bg-teal-800"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed")
                          }
                        >
                          {letter}
                        </button>
                      ))}
                      <button
                        onClick={() => handleVirtualKeyPress("BACKSPACE")}
                        disabled={!selectedCell}
                        className={
                          "w-full aspect-square rounded text-sm font-semibold transition-colors flex items-center justify-center col-span-2 " +
                          (selectedCell
                            ? "bg-red-600 text-white hover:bg-red-700 active:bg-red-800"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed")
                        }
                      >
                        <Delete className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Clues */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Clues</h3>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-2">Across</h4>
                <div className="space-y-2">
                  {puzzleData.words
                    .filter((w) => w.direction === "across")
                    .sort((a, b) => a.number - b.number)
                    .map((word) => (
                      <div key={word.number} className="text-sm text-gray-600">
                        <span className="font-semibold">{word.number}.</span>{" "}
                        {word.clue}
                      </div>
                    ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Down</h4>
                <div className="space-y-2">
                  {puzzleData.words
                    .filter((w) => w.direction === "down")
                    .sort((a, b) => a.number - b.number)
                    .map((word) => (
                      <div key={word.number} className="text-sm text-gray-600">
                        <span className="font-semibold">{word.number}.</span>{" "}
                        {word.clue}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalCrossword;
